import * as React from 'react';

import {
  Button,
  Content,
  DataGrid,
  DataGridColumn,
  Footer,
  humanReadableCurrency,
  humanReadableDateRange,
  humanReadableNumber,
  Pagination,
} from '@rematter/ui-web';
import { usePreferredLanguage } from '@uidotdev/usehooks';
import { useAtom, useAtomValue } from 'jotai';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { Eye } from 'lucide-react';

import { UsageMetricsRecord } from '../../_graphql/querySystemUsageMetrics';
import * as state from '../../_state/usageList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { adminRoutes } from '../../routes';

import { UsageFilterBar } from './UsageFilterBar';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const AIUsageView: React.FC = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const [limit = 25, setLimit] = useAtom(state.$usageLimit);
  const [offset, setOffset] = useAtom(state.$usageOffset);

  const preferredLocale = usePreferredLanguage();

  const {
    data: results,
    isLoading,
    error,
  } = useAtomValue(state.$remoteSystemUsageMetrics);

  const metrics = React.useMemo(
    () => results?.entries ?? [],
    [results?.entries],
  );
  const total = results?.total ?? 0;

  const handleViewDetails = React.useCallback(
    (organizationId: string) => {
      navigate(adminRoutes.aiUsageOrganizationDetail, {
        params: { organizationId },
      });
    },
    [navigate],
  );

  const columns: DataGridColumn<UsageMetricsRecord>[] = React.useMemo(
    () => [
      {
        key: 'organizationId',
        header: t.aiManagement.usage.header.organization,
        width: 200,
        minWidth: 150,
        cell: (cell) => (
          <div className="font-medium text-neutral/90">
            {cell.row.organization.name}
          </div>
        ),
      },
      {
        key: 'modelProvider',
        header: t.aiManagement.usage.header.modelProvider,
        width: 120,
        minWidth: 100,
        cell: (cell) => (
          <div>{upperFirst(camelCase(cell.row.modelProvider))}</div>
        ),
      },
      {
        key: 'model',
        header: t.aiManagement.usage.header.model,
        width: 200,
        minWidth: 150,
        cell: (cell) => (
          <div className="font-mono text-sm">{cell.row.model}</div>
        ),
      },
      {
        key: 'totalTokens',
        header: t.aiManagement.usage.header.totalTokens,
        width: 120,
        minWidth: 100,
        align: 'right',
        cell: (cell) => (
          <div>
            {humanReadableNumber(cell.row.totalTokens, {
              locale: preferredLocale,
            })}
          </div>
        ),
      },
      {
        key: 'totalCost',
        header: t.aiManagement.usage.header.totalCost,
        width: 120,
        minWidth: 100,
        align: 'right',
        cell: (cell) => (
          <div>
            {humanReadableCurrency(cell.row.totalCost.amount, {
              locale: preferredLocale,
              currencyCode: cell.row.totalCost.currencyCode,
            })}
          </div>
        ),
      },
      {
        key: 'recordCount',
        header: t.aiManagement.usage.header.recordCount,
        width: 100,
        minWidth: 80,
        align: 'right',
        cell: (cell) => (
          <div>
            {humanReadableNumber(cell.row.recordCount, {
              locale: preferredLocale,
            })}
          </div>
        ),
      },
      {
        key: 'period',
        header: t.aiManagement.usage.header.period,
        width: 200,
        minWidth: 150,
        cell: (cell) => (
          <div className="text-sm">
            {humanReadableDateRange(cell.row.periodStart, cell.row.periodEnd, {
              locale: preferredLocale,
            })}
          </div>
        ),
      },
      {
        key: 'actions',
        header: t.aiManagement.usage.header.actions,
        width: 100,
        minWidth: 100,
        align: 'center',
        cell: (cell) => (
          <Button
            size="small"
            variant="ghost"
            icon={<Eye className="size-4" />}
            onPress={() => handleViewDetails(cell.row.organization.id)}
          >
            {t.aiManagement.usage.actions.viewDetails}
          </Button>
        ),
      },
    ],
    [t, preferredLocale, handleViewDetails],
  );

  const handlePaginationChange = ({
    limit: newLimit,
    offset: newOffset,
  }: {
    limit: number;
    offset: number;
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  return (
    <div className="@AIUsageView flex h-full flex-col">
      <UsageFilterBar />

      <Content className="h-full border-t border-neutral/10">
        <DataGrid
          columns={columns}
          dataSource={metrics}
          fullWidth
          isLoading={isLoading}
          hasError={!!error}
          emptyPanelProps={{
            title: t.aiManagement.usage.empty.title,
            description: t.aiManagement.usage.empty.description,
          }}
        />
      </Content>

      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}
    </div>
  );
};
